import { Box, Stack, Typography, Chip, Divider } from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { useContractLaserSummary } from "../hooks/useContractLaser";

const NO_DECIMALS = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

interface ContractLasersSummaryCardProps {
  policyId: string;
}

/**
 * Portfolio summary for contract lasers + full exclusions on a policy
 * (Feature #5287, Story #5299). Fed by the contract-lasers-summary endpoint:
 * shows count and exposure (total contract deductible + spend over deductible).
 * Totals only: the claimants are the rows of the Lasered Claimants grid below.
 *
 * Renders nothing when no claimant has a contract laser or exclusion.
 */
export const ContractLasersSummaryCard = ({ policyId }: ContractLasersSummaryCardProps) => {
  const { summary, isLoading } = useContractLaserSummary(policyId, !!policyId);

  if (isLoading || !summary) return null;
  if (summary.contract_lasered_count === 0 && summary.excluded_count === 0) return null;

  return (
    <ShowAccordionSection label="Contract Lasers" defaultExpanded>
      <Box sx={{ p: 2 }} bgcolor="background.paper">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <GavelIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Chip
            label={`${summary.contract_lasered_count} contract laser${summary.contract_lasered_count !== 1 ? "s" : ""}`}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          {summary.excluded_count > 0 && (
            <Chip
              label={`${summary.excluded_count} excluded`}
              size="small"
              color="error"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Stack>

        <StatRow label="Total Contract Deductible" value={formatUSD(summary.total_contract_deductible, NO_DECIMALS)} />
        <Divider />
        <StatRow
          label="Total Spend Over Deductible"
          value={formatUSD(summary.total_spend_over_deductible, NO_DECIMALS)}
        />
      </Box>
    </ShowAccordionSection>
  );
};

const StatRow = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} color="text.primary">
      {value}
    </Typography>
  </Stack>
);
